import { useState, FormEvent } from 'react';
import { useQuickEnquiry } from '@/hooks/QuickEnquiryContext';

const quickServices = [
  'YouTube Services',
  'Music Production',
  'Audio Recording',
  'Video Production',
  'Artist Management',
  'Digital Promotion',
];

function InquirySection({
  visible = true,
}: {
  visible?: boolean;
}) {
    const [heroName, setHeroName] = useState('');
  const [heroPhone, setHeroPhone] = useState('');
  const [heroService, setHeroService] = useState('');
  const [heroSent, setHeroSent] = useState(false);

  const { openEnquiry } = useQuickEnquiry();

  const handleHeroSubmit = (e: FormEvent) => {
    e.preventDefault();

    const text = `New Website Enquiry 🚀

Name: ${heroName}
Phone: ${heroPhone}
Service: ${heroService || 'Not specified'}
Message: Enquiry from homepage hero form.`;

    const encoded = encodeURIComponent(text);

    const waUrl = `https://wa.me/919990194423?text=${encoded}`;

    setHeroSent(true);

    setTimeout(() => window.open(waUrl, '_blank'), 600);
  };
  
  return (
    <>
        <div className={`transition-all duration-700 delay-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="bg-[#0d0d0d]/80 backdrop-blur-md border border-[#d4af37]/20 rounded-3xl p-7 max-w-md ml-auto">
              {/* Card header */}
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-green-500/20 border border-green-500/30">
                  <i className="ri-whatsapp-line text-green-400 text-lg"></i>
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-white leading-tight">Quick Enquiry</h3>
                  <p className="text-white/40 text-xs">Get a response within hours</p>
                </div>
                <div className="ml-auto flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
                  <span className="text-green-400 text-xs font-medium">Online</span>
                </div>
              </div>

              {heroSent ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-500/15 border border-green-500/30 mx-auto mb-4">
                    <i className="ri-whatsapp-line text-green-400 text-3xl"></i>
                  </div>
                  <h4 className="font-heading text-lg font-bold text-white mb-1">Opening WhatsApp!</h4>
                  <p className="text-white/50 text-sm">Your enquiry is ready to send.</p>
                  <button
                    onClick={() => setHeroSent(false)}
                    className="mt-5 px-5 py-2 rounded-full text-xs text-[#d4af37] border border-[#d4af37]/30 hover:bg-[#d4af37]/10 transition-all cursor-pointer whitespace-nowrap"
                  >
                    Send Another
                  </button>
                </div>
              ) : (
                <form onSubmit={handleHeroSubmit} className="space-y-3">
                  <div>
                    <input
                      type="text"
                      required
                      value={heroName}
                      onChange={(e) => setHeroName(e.target.value)}
                      placeholder="Your Name *"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#d4af37]/50 transition-colors"
                    />
                  </div>
                  <div>
                    <input
                      type="tel"
                      required
                      value={heroPhone}
                      onChange={(e) => setHeroPhone(e.target.value)}
                      placeholder="Phone Number *"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#d4af37]/50 transition-colors"
                    />
                  </div>
                  <div>
                    <select
                      value={heroService}
                      onChange={(e) => setHeroService(e.target.value)}
                      className="w-full bg-[#1a1a1a] border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#d4af37]/50 transition-colors text-white/80"
                    >
                      <option value="" className="bg-[#1a1a1a]">Select Service (optional)</option>
                      {quickServices.map((s) => (
                        <option key={s} value={s} className="bg-[#1a1a1a]">{s}</option>
                      ))}
                    </select>
                  </div>

                  <button
                    type="submit"
                    className="w-full py-3.5 rounded-xl text-sm font-semibold text-white whitespace-nowrap bg-green-500 hover:bg-green-600 cursor-pointer transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
                  >
                    <i className="ri-whatsapp-line text-base"></i>
                    Send Enquiry on WhatsApp
                  </button>

                  <div className="flex items-center gap-2 pt-1">
                    <div className="flex-1 h-px bg-white/10"></div>
                    <span className="text-white/30 text-xs">or</span>
                    <div className="flex-1 h-px bg-white/10"></div>
                  </div>

                  <button
                    type="button"
                    onClick={() => openEnquiry(heroService)}
                    className="w-full py-3 rounded-xl text-sm font-medium text-[#d4af37] whitespace-nowrap border border-[#d4af37]/30 hover:bg-[#d4af37]/10 cursor-pointer transition-all flex items-center justify-center gap-2"
                  >
                    <i className="ri-file-list-3-line"></i>
                    Full Enquiry Form
                  </button>
                </form>
              )}

              {/* Trust badges */}
              <div className="flex items-center justify-center gap-4 mt-5 pt-4 border-t border-white/5">
                <div className="flex items-center gap-1.5 text-white/40 text-xs">
                  <i className="ri-shield-check-line text-[#d4af37]"></i>
                  100% Private
                </div>
                <div className="flex items-center gap-1.5 text-white/40 text-xs">
                  <i className="ri-time-line text-[#d4af37]"></i>
                  Reply in Hours
                </div>
                <div className="flex items-center gap-1.5 text-white/40 text-xs">
                  <i className="ri-star-line text-[#d4af37]"></i>
                  Free Consult
                </div>
              </div>
              </div>
              </div>
      
    </>
  )
}

export default InquirySection
